import { useEffect, useCallback, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Hook to warn users about unsaved changes when navigating away
 *
 * This hook intercepts navigation attempts and shows a warning dialog
 * when there are unsaved changes. It handles:
 * - In-app navigation (via link click interception)
 * - Browser back/forward buttons (via popstate)
 * - Page refresh/close (beforeunload)
 *
 * @param {boolean} hasUnsavedChanges - Whether the form has unsaved changes
 * @param {string} message - Custom message to show in the dialog
 * @returns {Object} - { isBlocked, pendingLocation, confirmNavigation, cancelNavigation, checkNavigation }
 */
export const useUnsavedChanges = (hasUnsavedChanges, message = 'You have unsaved changes. Are you sure you want to leave?') => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isBlocked, setIsBlocked] = useState(false);
  const [pendingLocation, setPendingLocation] = useState(null);
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);

  // Keep ref in sync with prop
  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  // Handle browser's beforeunload event for page refreshes/closes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChangesRef.current) {
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [message]);

  // Handle browser back/forward button with popstate
  useEffect(() => {
    const handlePopState = (e) => {
      if (hasUnsavedChangesRef.current) {
        // Push the current location back to prevent navigation
        window.history.pushState(null, '', location.pathname);
        setPendingLocation({ pathname: 'back' });
        setIsBlocked(true);
      }
    };

    // Push current state to allow us to intercept back button
    window.history.pushState(null, '', location.pathname);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [location.pathname]);

  // Intercept all link clicks for navigation blocking
  useEffect(() => {
    const handleClick = (e) => {
      // Only intercept if we have unsaved changes
      if (!hasUnsavedChangesRef.current) return;

      // Find the closest anchor element
      const anchor = e.target.closest('a');
      if (!anchor) return;

      // Get the href
      const href = anchor.getAttribute('href');
      if (!href) return;

      // Skip external links, hash links, and same-page links
      if (href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('#')) return;
      if (href === location.pathname) return;

      // Block the navigation
      e.preventDefault();
      e.stopPropagation();
      setPendingLocation({ pathname: href });
      setIsBlocked(true);
    };

    // Use capture phase to intercept before React Router handles the click
    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [location.pathname]);

  // Function to check if navigation should be blocked
  const checkNavigation = useCallback((to) => {
    if (hasUnsavedChangesRef.current && to !== location.pathname) {
      setPendingLocation({ pathname: to });
      setIsBlocked(true);
      return true; // Navigation blocked
    }
    return false; // Navigation allowed
  }, [location.pathname]);

  // Confirm navigation - proceed with leaving
  const confirmNavigation = useCallback(() => {
    setIsBlocked(false);
    if (pendingLocation) {
      if (pendingLocation.pathname === 'back') {
        window.history.back();
      } else {
        navigate(pendingLocation.pathname);
      }
      setPendingLocation(null);
    }
  }, [navigate, pendingLocation]);

  // Cancel navigation - stay on the page
  const cancelNavigation = useCallback(() => {
    setIsBlocked(false);
    setPendingLocation(null);
  }, []);

  return {
    isBlocked,
    pendingLocation,
    confirmNavigation,
    cancelNavigation,
    checkNavigation,
    message
  };
};

export default useUnsavedChanges;
